import { Component, inject, ChangeDetectionStrategy } from "@angular/core";
import {
    combineLatest,
    map,
    Observable,
    startWith,
    switchMap
} from "rxjs";
import { ApiClientService } from "~app/common/services/api-client.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Alert, GetVirkeskopAlertsQueryResult } from "~app/features/virkeskop/services/virkeskop.service";
import { DashboardService } from "../../dashboard.service";
import { AlertItem, AlertListComponent } from "../alert-list/alert-list.component";
import { MessageScopes } from "../alert-list/message-scopes";

const mapVirkeskopAlertsQueryResultToAlertItems = (result: GetVirkeskopAlertsQueryResult): AlertItem[] => result.alerts.map(alert => ({
    title: alert.title,
    message: alert.message,
    timestamp:  ObjectUtils.hasValue(alert.occured) ? new Date(alert.occured) : undefined,
    severity: alert.severity,
    cssClass: alert.metadata?.cssClass,
    links: [
        {
            text: alert.subEntityLinkName,
            resource: `virkeskop/avtal/${result.virkeskopsavtalId}`,
            queryParams: {
                kontraktId: alert.subEntityId?.toString()
            }
        }
    ]
}));

const mapLeverantorAlertsQueryResultToAlertItems = (result: GetLeverantorErrorsQueryResult): AlertItem[] => result.alerts.map(alert => ({
    title: result.namn,
    message: alert.message,
    timestamp: ObjectUtils.hasValue(alert.occured) ? new Date(alert.occured) : undefined,
    severity: alert.severity,
    cssClass: alert.metadata?.cssClass,
    links: [
        {
            text: result.namn,
            resource: `virkesleverantor/${result.virkesleverantorId}`
        }
    ]
}));

type GetLeverantorErrorsQueryResult = {
    virkesleverantorId: number;
    namn: string;
    alerts: Alert[];
};

@Component({
    selector: "rask-pagaende-affarer",
    imports: [AlertListComponent],
    templateUrl: "./pagaende-affarer.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./pagaende-affarer.component.scss"
})
export class PagaendeAffarerComponent {
    protected events$: Observable<AlertItem[]>;

    private readonly apiClientService = inject(ApiClientService);
    private readonly dashboardService = inject(DashboardService);

    private readonly userId$ = this.dashboardService.user$.pipe(
        map(user => user.id)
    );

    constructor() {
        this.events$ = this.userId$.pipe(
            switchMap(userId => this.getAlerts(userId))
        );
    }

    private getAlerts(userId: number): Observable<AlertItem[]> {
        const virkeskopError = this.apiClientService.get<GetVirkeskopAlertsQueryResult[]>(`virkeskoperror/${userId}?scope=${MessageScopes.PagaendeAffarer}`).pipe(
            map(res => res.flatMap(x => mapVirkeskopAlertsQueryResultToAlertItems(x)))
        );

        const leverantorError = this.apiClientService.get<GetLeverantorErrorsQueryResult[]>(`leverantorerror/${userId}?scope=${MessageScopes.PagaendeAffarer}`).pipe(
            map(res => res.flatMap(x => mapLeverantorAlertsQueryResultToAlertItems(x)))
        );

        return combineLatest([
            virkeskopError,
            leverantorError
        ]).pipe(
            map(([virkeskopAlerts, leverantorAlerts]) => [
                ...virkeskopAlerts,
                ...leverantorAlerts
            ]),
            startWith(null)
        );
    }
}
